import { computed, ref } from 'vue';
import type { Router } from 'vue-router';
import { formatDateForApi, parseCivilDate } from '@/shared/lib';
import { trainingApi, type CreateOpenTrainingPayload, type TrainingDetail } from '@/entities/Training';
import { useOpenTrainingInstructor } from './useOpenTrainingInstructor';
import { useTrainingFormSubmit } from './useTrainingFormSubmit';
import { useTrainingSchedulingWithCollaborators } from './useTrainingSchedulingWithCollaborators';

export type OpenTrainingCertificateType = 'Automático' | 'Externo';

export interface UseOpenTrainingFormEditContext {
  trainingId: number;
  initial: TrainingDetail;
  /**
   * Captured synchronously by the edit dispatcher before its await, since this composable
   * is instantiated after one — useRouter() would silently resolve to undefined by then.
   */
  router?: Router;
}

export function useOpenTrainingForm(
  instructor?: ReturnType<typeof useOpenTrainingInstructor>,
  editContext?: UseOpenTrainingFormEditContext
) {
  const { saving, submit: submitTraining } = useTrainingFormSubmit(editContext?.router);

  // OpenTrainingForm.vue also calls load() from its own onMounted (needed for the create
  // flow's load trigger), but in edit mode useEditTraining.ts's dispatcher already awaited
  // load() once before that component mounts. Without this flag, the redundant call would
  // reapply editContext.initial and re-run initializeParticipants's network fetch for no
  // reason.
  let editContextApplied = false;

  const title = ref('');
  const notes = ref('');
  const scheduledDate = ref<Date | null>(null);
  const certificateRequired = ref<boolean | null>(null);
  const certificateType = ref<OpenTrainingCertificateType | null>(null);
  const workloadHours = ref<number>(0);

  const {
    scheduledByMemberId,
    scheduledByPositionId,
    members,
    loading,
    collaborators,
    visibleMemberIds,
    scheduledByPositions,
    memberOptions,
    load: loadScheduling,
    setScheduledByMemberId,
    initializeParticipants,
  } = useTrainingSchedulingWithCollaborators();

  const resolvedInstructor = instructor ?? useOpenTrainingInstructor(members);

  function applyInitial(initial: TrainingDetail): void {
    title.value = initial.title;
    notes.value = initial.notes ?? '';
    scheduledByMemberId.value = initial.scheduling.responsible?.id ?? null;
    scheduledByPositionId.value = initial.scheduling.position?.id ?? null;
    scheduledDate.value = parseCivilDate(initial.scheduling.date);
    certificateRequired.value = initial.certificate?.required ?? null;
    certificateType.value = (initial.certificate?.type as OpenTrainingCertificateType | null) ?? null;
    workloadHours.value = initial.certificate?.workloadHours ?? 0;

    const instructorType = initial.instructorType;
    if (instructorType === 'INTERNAL') {
      resolvedInstructor.mode.value = 'INTERNAL';
      resolvedInstructor.internal.rows.value = initial.instructors.map((session) => ({
        id: session.sessionId,
        responsibleMemberId: session.memberId,
        responsibleUserPositionId: session.position?.id ?? null,
        trainingDate: parseCivilDate(session.trainingDate),
      }));
    } else if (instructorType === 'EXTERNAL_INDIVIDUAL') {
      resolvedInstructor.mode.value = 'EXTERNAL';
      resolvedInstructor.externalType.value = 'EXTERNAL_INDIVIDUAL';
      resolvedInstructor.externalIndividual.rows.value = initial.instructors.map((session, index) => ({
        id: index + 1,
        cpf: session.cpf ?? '',
        profession: session.profession ?? '',
        name: session.externalInstructorName ?? '',
        trainingDate: parseCivilDate(session.trainingDate),
        curriculumObjectName: session.curriculumObjectName,
        curriculumFileName: null,
        uploadProgress: 0,
        uploadStatus: 'idle',
        cpfError: null,
        professionError: null,
        nameError: null,
      }));
    } else if (instructorType === 'EXTERNAL_COMPANY') {
      resolvedInstructor.mode.value = 'EXTERNAL';
      resolvedInstructor.externalType.value = 'EXTERNAL_COMPANY';
      resolvedInstructor.externalCompany.cnpj.value = initial.externalInstructorCnpj ?? '';
      resolvedInstructor.externalCompany.companyName.value =
        initial.externalInstructorCompanyName ?? '';
      resolvedInstructor.externalCompany.rows.value = initial.instructors.map((session, index) => ({
        id: index + 1,
        name: session.externalInstructorName ?? '',
        trainingDate: parseCivilDate(session.trainingDate),
        curriculumObjectName: session.curriculumObjectName,
        curriculumFileName: null,
        uploadProgress: 0,
        uploadStatus: 'idle',
        nameError: null,
      }));
    }
  }

  async function load(): Promise<void> {
    const applyEditContext = editContext && !editContextApplied ? editContext : null;
    if (applyEditContext) {
      editContextApplied = true;
      applyInitial(applyEditContext.initial);
    }
    await loadScheduling();
    if (applyEditContext) {
      await initializeParticipants(
        applyEditContext.initial.positions.map((position) => position.id),
        applyEditContext.initial.trainees.map((trainee) => trainee.memberId)
      );
    }
  }

  function setCertificateRequired(required: boolean | null): void {
    certificateRequired.value = required;
    if (!required) {
      certificateType.value = null;
    }
  }

  const certificateFieldsEnabled = computed(() => certificateRequired.value === true);

  const canSave = computed(() => {
    if (
      title.value.trim() === '' ||
      scheduledByMemberId.value === null ||
      scheduledByPositionId.value === null ||
      scheduledDate.value === null ||
      visibleMemberIds.value.length === 0 ||
      certificateRequired.value === null ||
      !resolvedInstructor.isComplete.value ||
      workloadHours.value < 1
    ) {
      return false;
    }

    if (certificateRequired.value && certificateType.value === null) {
      return false;
    }

    return true;
  });

  function buildSubmitPayload(): CreateOpenTrainingPayload | null {
    if (
      scheduledByMemberId.value === null ||
      scheduledByPositionId.value === null ||
      scheduledDate.value === null ||
      visibleMemberIds.value.length === 0 ||
      certificateRequired.value === null ||
      workloadHours.value < 1
    ) {
      return null;
    }

    if (certificateRequired.value && certificateType.value === null) {
      return null;
    }

    const instructorFields = resolvedInstructor.buildPayload();
    if (instructorFields === null) return null;

    const apiScheduledDate = formatDateForApi(scheduledDate.value);
    if (apiScheduledDate === null) return null;

    const participants = visibleMemberIds.value.map(Number);
    if (participants.some((memberId) => Number.isNaN(memberId))) return null;

    const positionIds = collaborators.selectedPositions.value.map(Number);
    if (positionIds.some((positionId) => Number.isNaN(positionId))) return null;

    return {
      title: title.value.trim(),
      notes: notes.value.trim() ? notes.value.trim() : undefined,
      scheduledBy: { memberId: scheduledByMemberId.value, positionId: scheduledByPositionId.value },
      plannedDate: apiScheduledDate,
      participants,
      positionIds,
      certificateRequired: certificateRequired.value,
      certificateType: certificateRequired.value ? (certificateType.value ?? undefined) : undefined,
      workloadHours: workloadHours.value,
      ...instructorFields,
    };
  }

  async function submit(onSuccess?: () => void): Promise<void> {
    if (editContext) {
      await submitTraining(
        canSave.value,
        buildSubmitPayload,
        (payload) => trainingApi.updateOpenTraining(editContext.trainingId, payload),
        { mode: 'edit', onFieldErrors: resolvedInstructor.applyFieldErrors, onSuccess }
      );
      return;
    }

    await submitTraining(
      canSave.value,
      buildSubmitPayload,
      (payload) => trainingApi.createOpenTraining(payload),
      { onFieldErrors: resolvedInstructor.applyFieldErrors }
    );
  }

  const snapshot = computed(() =>
    JSON.stringify({
      title: title.value.trim(),
      notes: notes.value.trim(),
      scheduledByMemberId: scheduledByMemberId.value,
      scheduledByPositionId: scheduledByPositionId.value,
      scheduledDate: scheduledDate.value ? formatDateForApi(scheduledDate.value) : null,
      certificateRequired: certificateRequired.value,
      certificateType: certificateType.value,
      workloadHours: workloadHours.value,
      mode: resolvedInstructor.mode.value,
      externalType: resolvedInstructor.externalType.value,
      internal: resolvedInstructor.internal.rows.value.map((row) => ({
        responsibleMemberId: row.responsibleMemberId,
        responsibleUserPositionId: row.responsibleUserPositionId,
        trainingDate: row.trainingDate ? formatDateForApi(row.trainingDate) : null,
      })),
      externalIndividual: resolvedInstructor.externalIndividual.rows.value.map((row) => ({
        cpf: row.cpf,
        profession: row.profession,
        name: row.name,
        trainingDate: row.trainingDate ? formatDateForApi(row.trainingDate) : null,
      })),
      externalCompany: {
        cnpj: resolvedInstructor.externalCompany.cnpj.value,
        companyName: resolvedInstructor.externalCompany.companyName.value,
        rows: resolvedInstructor.externalCompany.rows.value.map((row) => ({
          name: row.name,
          trainingDate: row.trainingDate ? formatDateForApi(row.trainingDate) : null,
        })),
      },
      visibleMemberIds: [...visibleMemberIds.value].sort(),
      positions: [...collaborators.selectedPositions.value].sort(),
    })
  );

  return {
    title,
    notes,
    scheduledByMemberId,
    scheduledByPositionId,
    scheduledDate,
    certificateRequired,
    certificateType,
    workloadHours,
    members,
    scheduledByPositions,
    memberOptions,
    certificateFieldsEnabled,
    loading,
    saving,
    canSave,
    instructor: resolvedInstructor,
    collaborators,
    snapshot,
    load,
    setScheduledByMemberId,
    setCertificateRequired,
    submit,
  };
}
